import React, { useEffect } from 'react';

import { MonitorView } from 'components/MonitorView';
import SceneSetView from 'components/SceneSetView';
import { DefaultOrientation } from 'components/SceneSetView/types';
import { MobileWebGateContent } from 'components/Screens/MobileWebGate';
import { matchPath, Redirect, useHistory } from 'cross-platform/react-router';
import { getCountryCookie, setCountryCookie } from 'lib/countryCookie';
import {
  COUNTRY_TO_DESTINATION,
  VANITY_TO_COUNTRY,
} from 'lib/countryCookie/types';

// Wraps the country selector SceneSet (/nhs or /nhs/:sceneSetId).
// If platgen_uk_country cookie is set, redirect straight to that country's vanity flow.
export const CountrySelectorGuard = (): React.ReactElement => {
  const history = useHistory();
  const country = getCountryCookie();

  if (country) {
    const productReference =
      matchPath<{ productReference: string }>(history.location.pathname, {
        path: '/:productReference',
      })?.params?.productReference ?? 'sleepio';
    const destination = COUNTRY_TO_DESTINATION[country];
    return <Redirect to={`/${productReference}/${destination}`} />;
  }

  // DEBUG: confirm selector is rendering (remove after verifying)
  if (typeof document !== 'undefined') {
    document.cookie = 'debug_selectorGuard=1; Path=/';
  }

  return (
    <MonitorView viewName="SceneSetView">
      <MobileWebGateContent>
        <SceneSetView defaultOrientation={DefaultOrientation.LANDSCAPE} />
      </MobileWebGateContent>
    </MonitorView>
  );
};

// Wraps country-specific vanity SceneSets (nhs-england, nhs-sleepio, wales, northern-ireland).
// On mount, extracts the vanity from the current pathname and writes platgen_uk_country
// so that CountrySelectorGuard can redirect on subsequent visits.
export const CountryVanityEffect = (): React.ReactElement => {
  const history = useHistory();
  // DEBUG: confirm component is rendering (remove after verifying)
  if (typeof document !== 'undefined') {
    document.cookie = 'debug_vanityEffect=1; Path=/';
  }

  useEffect(() => {
    const pathname = history.location.pathname;
    const match = matchPath<{ productReference: string; vanity: string }>(
      pathname,
      { path: '/:productReference/:vanity' }
    );
    const vanity = match?.params?.vanity;
    if (vanity && VANITY_TO_COUNTRY[vanity]) {
      setCountryCookie(VANITY_TO_COUNTRY[vanity]);
    }
  }, [history.location.pathname]);

  return (
    <MonitorView viewName="SceneSetView">
      <MobileWebGateContent>
        <SceneSetView defaultOrientation={DefaultOrientation.LANDSCAPE} />
      </MobileWebGateContent>
    </MonitorView>
  );
};
